import { AskAiMessageBubble } from '@/features/assistant/_components/ask-ai-message-bubble';
import type { ConversationMessage } from '@/features/conversations/api';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

import type { ConversationHandlerSource } from './conversation-handler-badge';
import {
  ConversationMessageMedia,
  shouldShowMessageText,
} from './conversation-message-media';
import { ConversationMessageSenderAvatar } from './conversation-message-sender-avatar';
import { MessageDeliveryStatus } from './message-delivery-status';

const URL_PATTERN = /(https?:\/\/[^\s]+)/g;

function isUrlSegment(part: string): boolean {
  return /^https?:\/\/\S+$/.test(part);
}

function renderMessageContent(content: string, isOutbound: boolean): ReactNode {
  const parts = content.split(URL_PATTERN);
  if (parts.length === 1) {
    return content;
  }

  return parts.map((part, index) => {
    if (isUrlSegment(part)) {
      return (
        <a
          key={`${part}-${index}`}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'underline underline-offset-2',
            isOutbound ? 'text-white' : 'text-[#007AFF]'
          )}
        >
          {part}
        </a>
      );
    }
    return <span key={`${part}-${index}`}>{part}</span>;
  });
}

function isOutboundMessage(role: string): boolean {
  return role === 'bot' || role === 'agent';
}

interface ConversationMobileMessageBubbleProps {
  message: ConversationMessage;
  conversation: ConversationHandlerSource;
  memberFirstNameById: Map<string, string>;
  memberFullNameById: Map<string, string>;
  memberImageById: Map<string, string | null>;
  showDelivered?: boolean;
}

export function ConversationMobileMessageBubble({
  message,
  conversation,
  memberFirstNameById,
  memberFullNameById,
  memberImageById,
  showDelivered = false,
}: ConversationMobileMessageBubbleProps) {
  const isOutbound = isOutboundMessage(message.role);
  // Claire is black; staff (and messages a person sent from the Page, which
  // sync in as `bot`) stay blue.
  const isSyncedFromHuman =
    (message.metadata as { synced?: boolean } | null)?.synced === true;
  const isClaire = message.role === 'bot' && !isSyncedFromHuman;
  const bubbleRole = isOutbound ? 'user' : 'assistant';
  const sender =
    message.role === 'user' ? 'customer' : isClaire ? 'claire' : 'staff';

  if (!isOutbound) {
    return (
      <div
        className="flex w-full justify-start"
        data-testid="message-bubble"
        data-sender={sender}
      >
        <AskAiMessageBubble variant={bubbleRole}>
          <div className="space-y-1.5">
            <ConversationMessageMedia message={message} />
            {shouldShowMessageText(message) && (
              <p className="whitespace-pre-wrap break-words">
                {renderMessageContent(message.content ?? '', false)}
              </p>
            )}
          </div>
        </AskAiMessageBubble>
      </div>
    );
  }

  return (
    <div
      className="flex w-full justify-end gap-2"
      data-testid="message-bubble"
      data-sender={sender}
    >
      <div className="flex min-w-0 flex-1 flex-col items-end gap-1">
        <AskAiMessageBubble
          variant={bubbleRole}
          className={isClaire ? 'bg-black text-white' : undefined}
        >
          <div className="space-y-1.5">
            <ConversationMessageMedia message={message} />
            {shouldShowMessageText(message) && (
              <p className="whitespace-pre-wrap break-words">
                {renderMessageContent(message.content ?? '', true)}
              </p>
            )}
          </div>
        </AskAiMessageBubble>
        <span className="text-[11px] leading-none text-[#8E8E93]">
          <MessageDeliveryStatus
            message={message}
            showReceipt={showDelivered}
          />
        </span>
      </div>
      <ConversationMessageSenderAvatar
        messageRole={message.role}
        conversation={conversation}
        memberFirstNameById={memberFirstNameById}
        memberFullNameById={memberFullNameById}
        memberImageById={memberImageById}
        className="mt-0.5"
      />
    </div>
  );
}
